import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { GraphQLFormattedError } from 'graphql';
import {
  FormChecklistTemplateOption,
  useFormChecklistTemplateLazyQuery,
  useFormChecklistTemplatesSearchListLazyQuery,
} from 'graphql-common';
import { FieldItem, Values } from '@lib/interfaces/form';
import Form, {
  ActionsProps,
} from '@lib/components/ReactHookForm/FormContainer';
import Modal from '@lib/components/Modal/Modal';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import { getFormChecklistsLoadOptions as getFormChecklistsLoadOptionsFn } from 'utils/fetch/searchLists/formChecklistsSearchList';
import { captureException } from '@lib/utils/captureException';
import FormActions from './FormChecklistSelectModalFormActions';
import getFormFields from './utils/getFormFields';

export interface FormProps {
  graphQLErrors?: readonly GraphQLFormattedError[];
  isEditForm?: boolean;
  isOpen: boolean;
  onCancel: () => void;
  onClose: () => void;
  onSubmit: (values: FormChecklistTemplateOption[]) => void;
  formRef: FormRefType;
}

export default function FormChecklistSelectModalForm(props: FormProps) {
  const { graphQLErrors, isOpen, onCancel, onClose, onSubmit, formRef } = props;
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [fetchFormChecklistTemplateLazyQuery] =
    useFormChecklistTemplateLazyQuery({ fetchPolicy: 'network-only' });
  const [fetchFormChecklistTemplatesSearchListLazyQuery] =
    useFormChecklistTemplatesSearchListLazyQuery();
  const getChecklistsLoadOptions = () =>
    getFormChecklistsLoadOptionsFn(
      fetchFormChecklistTemplatesSearchListLazyQuery,
    );

  const fields: FieldItem[] = getFormFields({
    getChecklistsLoadOptions,
    t,
  });

  const defaultFormValues = { data: undefined };

  const actionsComponent: (
    actionsComponentProps: ActionsProps,
  ) => React.ReactElement = (actionsComponentProps) => (
    <FormActions {...actionsComponentProps} onCancel={onCancel} />
  );

  const onSubmitHandler = (values: Values) => {
    setIsLoading(true);
    const id = _get(values, 'data.value', '') as string;
    if (id) {
      fetchFormChecklistTemplateLazyQuery({
        variables: { id },
      }).then((res) => {
        const options = _get(
          res,
          'data.data.options',
          [],
        ) as FormChecklistTemplateOption[];
        onSubmit(options);
        setIsLoading(false);
        onCancel();
      });
    } else {
      captureException(
        'FormChecklistSelectModalForm: onSubmitHandler: id is not defined',
      );
      setIsLoading(false);
    }
  };
  const actionsComponentProps = { onCancel };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      caption={t('select-options-list-template-title')}
    >
      <Form
        actions={actionsComponent}
        actionsComponentProps={actionsComponentProps}
        defaultValues={defaultFormValues}
        fields={fields}
        formId="checklist-select-modal-form"
        formRef={formRef}
        graphQLErrors={graphQLErrors}
        loading={isLoading}
        onSubmit={onSubmitHandler}
      />
    </Modal>
  );
}
